import { formatEUR } from '../utils/money';
import { resolveReportStepLink } from './reportModulePaths';

export function normalizeAssessment(assessment) {
  if (!assessment || typeof assessment !== 'object') {
    return { good: [], bad: [], improve: [] };
  }
  return {
    good: Array.isArray(assessment.good) ? assessment.good : [],
    bad: Array.isArray(assessment.bad) ? assessment.bad : [],
    improve: Array.isArray(assessment.improve) ? assessment.improve : [],
  };
}

export function pickHighlights(assessment) {
  const a = normalizeAssessment(assessment);
  const highlights = [];
  if (a.good[0]) highlights.push({ tone: 'good', item: a.good[0] });
  if (a.bad[0]) highlights.push({ tone: 'bad', item: a.bad[0] });
  if (a.improve[0]) highlights.push({ tone: 'improve', item: a.improve[0] });
  return highlights;
}

export function consecutiveGrowthMonths(timeline) {
  if (!Array.isArray(timeline) || timeline.length < 2) return 0;
  let count = 0;
  for (let i = timeline.length - 1; i > 0; i -= 1) {
    const curr = Number(timeline[i]?.patrimonio_eur);
    const prev = Number(timeline[i - 1]?.patrimonio_eur);
    if (Number.isFinite(curr) && Number.isFinite(prev) && curr > prev) {
      count += 1;
    } else {
      break;
    }
  }
  return count;
}

function formatDelta(value) {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const sign = n > 0 ? '+' : '';
  return `${sign}${formatEUR(n)}`;
}

/**
 * Meta chips do veredito (património, poupança, streak/selfies).
 */
export function buildVerdictMeta(report) {
  const chips = [];
  const netDelta = report?.patrimonio?.delta_eur;
  const deltaLabel = formatDelta(netDelta);
  if (deltaLabel) {
    chips.push({
      key: 'patrimonio',
      labelKey: 'patrimonio',
      value: deltaLabel,
      tone: netDelta == null || netDelta >= 0 ? 'positive' : 'caution',
    });
  }

  const savingsRate =
    report?.cycle_summary?.savings_rate_pct ?? report?.patrimonio?.savings_rate_pct;
  if (savingsRate != null && Number.isFinite(Number(savingsRate))) {
    chips.push({
      key: 'savings',
      labelKey: 'savings',
      value: `${Number(savingsRate).toFixed(0)}%`,
      tone: Number(savingsRate) >= 15 ? 'positive' : 'caution',
    });
  }

  const streak = consecutiveGrowthMonths(report?.timeline);
  const selfieNumber = report?.selfie?.number;
  if (streak >= 2) {
    chips.push({
      key: 'streak',
      labelKey: 'streak',
      value: String(streak),
      tone: 'positive',
    });
  } else if (selfieNumber != null) {
    chips.push({
      key: 'selfies',
      labelKey: 'selfies',
      value: String(selfieNumber),
      tone: 'neutral',
    });
  }

  return chips.slice(0, 3);
}

export function firstVerdictParagraph(body) {
  if (!body) return '';
  return String(body)
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)[0] || '';
}

/**
 * Próximos passos: API next_steps → fallback improve com related_module.
 */
export function buildNextSteps(report) {
  const fromApi = Array.isArray(report?.next_steps) ? report.next_steps : [];
  if (fromApi.length) {
    return fromApi.slice(0, 3).filter((step) => step?.title && resolveReportStepLink(step));
  }

  const assessment = normalizeAssessment(report?.assessment);
  const steps = [];
  for (const item of assessment.improve) {
    const link = resolveReportStepLink(item);
    if (link) {
      steps.push({
        title: item.title,
        body: item.body,
        related_module: item.related_module,
      });
    }
    if (steps.length >= 3) break;
  }
  return steps;
}

export function hasAiNarrative(report) {
  const assessment = normalizeAssessment(report?.assessment);
  return (
    Boolean(report?.consultant_verdict?.headline && report?.consultant_verdict?.body) ||
    Boolean(report?.overall_tone_summary) ||
    (report?.category_organization?.status === 'needs_review' &&
      (report?.category_organization?.notes?.length || 0) > 0) ||
    assessment.good.length + assessment.bad.length + assessment.improve.length > 0
  );
}
